import { forwardRef, useEffect, useState, useRef } from "react";
import { useSlidingPill } from "@/components/shared/Pill";
import { useCountUp } from "@/lib/countUp";
import { Sheet, SheetClose, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

// Tracks a <768px viewport so mobile/desktop variants render exactly once.
// 237: přesný doplněk Tailwind `md:` (min-width: 768px) — při zlomkové šířce (náhled
// tabletu v Emergentu ~767,5 px) dřív ukázal mobilní lišty i počítačový obsah zároveň.
export const PHONE_MQ = "not all and (min-width: 768px)";
export function useIsMobile() {
  const [mobile, setMobile] = useState(typeof window !== "undefined" && window.matchMedia(PHONE_MQ).matches);
  useEffect(() => {
    const mq = window.matchMedia(PHONE_MQ);
    const on = (e) => setMobile(e.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return mobile;
}

// Úzké rozhraní = Kanban se nevejde (mobil i tablet na výšku). Nad 1024 px
// nabízíme Kanban, pod ním Seznam s tahy prstem.
export function useIsNarrow() {
  const [narrow, setNarrow] = useState(typeof window !== "undefined" && window.matchMedia("(max-width: 1023px)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 1023px)");
    const on = (e) => setNarrow(e.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return narrow;
}

// Spodní šuplík se safe-area odsazením a zavíráním tahem dolů (i křížkem).
// Používá se pro detail/úpravu/smazání úkolu na mobilu.
// Kulatý křížek pro hlavičky šuplíků a oken (175) — vždy vpravo, v řádku s ostatním.
export const CloseButton = forwardRef(function CloseButton({ testid = "sheet-close", className = "", ...rest }, ref) {
  return (
    <button ref={ref} type="button" aria-label="Zavřít" data-testid={testid} {...rest}
      className={`w-10 h-10 shrink-0 rounded-full bg-secondary text-foreground grid place-items-center hover:bg-secondary/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${className}`}>
      <X size={18} />
    </button>
  );
});

// Listování „předchozí / další" (175): obě šipky u sebe jako jedno tlačítko + pořadí.
// Patří do LEVÉ části hlavičky; vpravo zůstává jen křížek.
export function PrevNextPager({ index, total, onPrev, onNext, testid = "pager" }) {
  const btn = "w-11 h-10 grid place-items-center text-foreground hover:bg-secondary disabled:opacity-30 disabled:hover:bg-transparent transition-colors motion-reduce:transition-none focus-visible:outline-none focus-visible:bg-secondary";
  return (
    <div className="flex items-center gap-2.5 min-w-0" data-testid={testid}>
      <div className="inline-flex shrink-0 rounded-full border border-border bg-card overflow-hidden">
        <button type="button" onClick={onPrev} disabled={index <= 0} aria-label="Předchozí" className={btn} data-testid={`${testid}-prev`}>
          <ChevronLeft size={19} />
        </button>
        <span className="w-px bg-border" aria-hidden />
        <button type="button" onClick={onNext} disabled={index >= total - 1} aria-label="Další" className={btn} data-testid={`${testid}-next`}>
          <ChevronRight size={19} />
        </button>
      </div>
      <span className="text-[13px] font-bold tabular-nums text-muted-foreground whitespace-nowrap">{index + 1} / {total}</span>
    </div>
  );
}

// `actions` = levá část hlavičky (např. PrevNextPager); vpravo je vždy jen křížek.
// Hlavička je přilepená nahoře — křížek i listování jsou po ruce i po odrolování.
export function BottomSheet({ open, onOpenChange, title, actions, children, testid }) {
  const [dy, setDy] = useState(0);
  const start = useRef(null);
  useEffect(() => { if (!open) setDy(0); }, [open]);
  const onDown = (e) => { start.current = e.clientY; e.currentTarget.setPointerCapture?.(e.pointerId); };
  const onMove = (e) => { if (start.current == null) return; const d = e.clientY - start.current; setDy(d > 0 ? d : 0); };
  const onUp = () => { if (start.current == null) return; const close = dy > 90; start.current = null; if (close) onOpenChange(false); else setDy(0); };
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      {/* Bez automatického zaměření prvního tlačítka — jinak po otevření svítí kroužek na šipce / křížku. */}
      <SheetContent side="bottom" data-testid={testid} hideClose onOpenAutoFocus={(e) => e.preventDefault()}
        style={dy ? { transform: `translateY(${dy}px)`, transition: "none" } : undefined}
        className="rounded-t-[24px] max-h-[85vh] overflow-y-auto p-4 pt-0 pb-[max(60px,env(safe-area-inset-bottom))]">
        <div className="sticky top-0 z-20 -mx-4 px-4 pt-2 pb-2 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
          <div onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}
            className="mx-auto mb-2 h-1.5 w-10 rounded-full bg-border touch-none cursor-grab" data-testid="sheet-grab" aria-hidden />
          <div className="flex items-center justify-between gap-3 min-h-[40px]" data-testid="sheet-header">
            <div className="min-w-0 flex-1">
              {actions || <SheetTitle className={title ? "text-lg font-bold truncate" : "sr-only"}>{title || "Detail"}</SheetTitle>}
            </div>
            {actions && <SheetTitle className="sr-only">{title || "Detail"}</SheetTitle>}
            <SheetClose asChild><CloseButton /></SheetClose>
          </div>
        </div>
        <div className="mt-1">{children}</div>
      </SheetContent>
    </Sheet>
  );
}

// Big page title (left) + primary actions (right). App-wide standard header.
export function PageHeader({ title, subtitle, actions, testid = "page-header" }) {
  return (
    <div className="flex items-start justify-between flex-wrap gap-4" data-testid={testid}>
      <div className="min-w-0">
        <h1 className="text-3xl font-black tracking-tighter uppercase">{title}</h1>
        {subtitle && <p className="text-muted-foreground mt-1">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
    </div>
  );
}

// Glass KPI card with a big number. Clickable when onClick provided.
export function StatCard({ icon: Icon, label, value, sub, subClass, title, active, danger, brand, valueClass, onClick, testid }) {
  const Comp = onClick ? "button" : "div";
  const valueCls = valueClass || (danger && value > 0 ? "text-destructive" : brand ? "text-brand" : "");
  const shown = useCountUp(value);   // 384: číslo napočítá
  return (
    <Comp onClick={onClick} data-testid={testid} title={title}
      className={`glass rounded-[14px] p-4 text-left transition-all min-w-0 ${onClick ? "hover:-translate-y-0.5" : ""} ${active ? "!border-brand !bg-brand/10" : ""}`}>
      <div className="flex items-center gap-2 text-muted-foreground text-[11px] font-bold uppercase tracking-widest mb-2">
        {Icon && <Icon size={14} />} {label}
      </div>
      <div className={`font-heading text-2xl sm:text-3xl font-extrabold tracking-tight whitespace-nowrap tabular-nums ${valueCls}`}>{shown}</div>
      {sub != null && sub !== "" && (
        <div className={`text-xs font-semibold mt-1 truncate ${subClass || "text-muted-foreground"}`}>{sub}</div>
      )}
    </Comp>
  );
}

// Pill switcher (glass) with optional counts and icons.
// Posuvná lišta záložek (tým, zakázka, pokladna, platby): pevná výška,
// rámeček zakulacený na obou koncích, text se nikdy nezalomí a jezdí jen
// vodorovně UVNITŘ rámečku. U okraje, kde je ještě další záložka, se text
// jemně ztrácí (maska, ne překryv — rámeček zůstane celý). Vybraná záložka
// se sama posune do středu (jen lišta, stránka neposkočí).
export function useScrollTabs(active) {
  const ref = useRef(null);
  const [edge, setEdge] = useState({ l: false, r: false });
  const measure = () => {
    const el = ref.current;
    if (!el) return;
    setEdge({ l: el.scrollLeft > 2, r: el.scrollLeft + el.clientWidth < el.scrollWidth - 2 });
  };
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const btn = el.querySelector('[data-state="active"], [aria-selected="true"]');
    if (btn) {
      const target = btn.offsetLeft - (el.clientWidth - btn.offsetWidth) / 2;
      const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
      el.scrollTo({ left: Math.max(0, target), behavior: reduce ? "auto" : "smooth" });
    }
    measure();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    return () => ro?.disconnect();
  }, [active]);
  const fade = `linear-gradient(90deg, ${edge.l ? "transparent 0, #000 20px" : "#000 0"}, ${edge.r ? "#000 calc(100% - 28px), transparent 100%" : "#000 100%"})`;
  return { ref, onScroll: measure, style: { WebkitMaskImage: fade, maskImage: fade } };
}

export const TAB_SHELL = "max-w-full min-w-0 rounded-full border border-border bg-card pp-tint p-1 overflow-hidden";
export const TAB_SCROLL = "flex flex-nowrap gap-1 overflow-x-auto overflow-y-hidden overscroll-x-contain touch-pan-x no-scrollbar rounded-full";
export const TAB_BTN = "shrink-0 h-9 px-4 rounded-full inline-flex items-center gap-1.5 whitespace-nowrap text-sm font-bold transition-colors";

export function TabSwitcher({ tabs, value, onChange, testid = "tab-switcher" }) {
  const st = useScrollTabs(value);
  const sp = useSlidingPill(value, tabs.map((t) => `${t.label}${t.count ?? ""}`).join("|"));   // 383: podklad dojede s pružinou
  const setRef = (el) => { st.ref.current = el; sp.ref.current = el; };
  return (
    <div className={`${TAB_SHELL} w-full md:w-fit`} data-testid={testid}>
      <div ref={setRef} onScroll={st.onScroll} style={st.style} role="tablist" className={`relative ${TAB_SCROLL}`}>
        {sp.pill("bg-primary")}
        {tabs.map((t) => {
          const Icon = t.icon;
          const on = value === t.value;
          return (
            <button key={t.value} type="button" role="tab" aria-selected={on} data-testid={t.testid} onClick={() => onChange(t.value)} data-pill-on={on || undefined}
              className={`relative z-[1] ${TAB_BTN} ${on ? `${sp.ready ? "" : "bg-primary"} text-primary-foreground` : "text-muted-foreground hover:text-foreground"}`}>
              {Icon && <Icon size={15} className="shrink-0" />}{t.label}{t.count != null ? ` (${t.count})` : ""}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Removable / toggle chip.
export function FilterChip({ label, active, onClick, onClear, testid }) {
  if (onClear) {
    return (
      <span className="inline-flex items-center gap-1.5 bg-secondary text-secondary-foreground text-xs font-semibold px-3 py-1.5 rounded-full">
        {label}
        <button onClick={onClear} className="hover:text-destructive" data-testid={testid || "chip-clear"}><XIcon /></button>
      </span>
    );
  }
  return (
    <button type="button" data-testid={testid} onClick={onClick}
      className={`px-3 py-1.5 rounded-full text-sm font-semibold border transition-colors ${active ? "bg-brand text-white border-brand" : "bg-secondary border-border text-muted-foreground hover:text-foreground"}`}>
      {label}
    </button>
  );
}

function XIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
  );
}
